<script setup lang="ts">
// menu da sidebar já filtrado pelas flags (paridade com o menu antigo)
const { sidebarMenu, isNotificationsVisible } = useUserV2Menu();

// Shell do Minha Conta V2: header + saldos + navegação + conteúdo.
// Mobile: fullscreen, com tabs Perfil | Dados | Notificações (UserV2Tabs) e a
// barra de saldos só no Perfil. Desktop: painel central (948px) SEM tabs — a
// navegação é a sidebar "Edite seus dados" (Perfil/Notificações viram itens) e
// a barra de saldos fica sempre visível no topo (frames "Perfil - Desktop").
interface Props {
  // título da subtela (linha "‹ Dados da conta"); vazio = tela raiz/tab
  screenTitle?: string;
  backTo?: string;
}

const props = withDefaults(defineProps<Props>(), {
  screenTitle: '',
  backTo: '/user/data',
});

const route = useRoute();
const { logout } = useAuthStore();
const { state, close, currentPath, getScreenForPath } = useUserV2Modal();
// isDesktop do useLayout() é 1024px (compartilhado com o resto do app, não
// dá pra mudar aqui) — o painel vira "desktop" a partir de 768px (md:), por
// isso usa isMobile (que já é <768) em vez de isDesktop
const { isMobile } = useLayout();

// mesmas opções do logout no header antigo (components/user/header/UserMenuItems.vue):
// toast "Você saiu da sua conta", redireciona pra home e trava a biometria
// (origin: 'user'). Fecha o painel primeiro pra não ficar sobre a home depois.
async function handleLogout() {
  close();
  await logout({ displayMessage: true, redirectToHome: true, origin: 'user' });
}

// "Sair da conta" agora pede confirmação (Figma 23239:78544) — antes
// deslogava direto no clique, sem chance de cancelar.
const isLogoutConfirmOpen = ref(false);

// Fecha ao clicar no backdrop (área escura em volta do painel). Só a partir
// de 768px (md:): abaixo disso o painel é fullscreen, então não há "fora"
// pra clicar.
function onBackdropClick() {
  if (!isMobile.value) {
    close();
  }
}

// tab ativa (mobile) derivada da tela aberta — evita prop em toda tela
const activeTab = computed<'profile' | 'data' | 'notifications'>(() => {
  const path = currentPath.value || route.path;

  if (path.endsWith('/user/profile')) return 'profile';
  if (path.endsWith('/user/notifications')) return 'notifications';

  return 'data';
});

// scroll + expansão da sidebar persistem entre as trocas de tela (cada tela
// remonta o Shell), pra navegação não "pular" pro topo ao clicar num submenu
const { expandedMenus, scrollTop } = useUserV2SidebarUi();
const asideRef = ref<HTMLElement | null>(null);

function onSidebarScroll() {
  if (asideRef.value) {
    scrollTop.value = asideRef.value.scrollTop;
  }
}

function toggleMenu(label: string) {
  if (expandedMenus.value.has(label)) {
    expandedMenus.value.delete(label);
  } else {
    expandedMenus.value.add(label);
  }
  // reatribui pra disparar reatividade do Set
  expandedMenus.value = new Set(expandedMenus.value);
}

function isMenuActive(to?: string) {
  if (!to) return false;

  // resolve pra tela em vez de comparar só o path: "Limites de proteção" e
  // "Auto-Exclusão" dividem o mesmo path (/user/account-protection) com
  // ?section= diferente — comparar path sozinho acendia os dois juntos.
  const screen = getScreenForPath(to);

  if (screen) {
    return state.value.screen === screen;
  }

  // to não é uma rota conhecida do modal (ex.: link fora do /user/*) —
  // fallback pra rota real (caso renderize fora do modal)
  return route.path.endsWith(to.replace(/\?.*$/, ''));
}

// Ao trocar de tela dentro do modal (ex.: "Ver mais" das Conquistas, que
// fica no fim do Perfil), o scroll ficava onde o usuário estava na tela
// anterior — a nova abria já rolada até o fim. Só o conteúdo remonta (:key
// no Modal.vue), os containers roláveis são os mesmos: overlay no mobile
// (tela inteira rola) e o miolo do <main> no desktop.
const overlayRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);

watch(
  () => state.value.screen,
  () => {
    nextTick(() => {
      if (overlayRef.value) overlayRef.value.scrollTop = 0;
      if (contentRef.value) contentRef.value.scrollTop = 0;
    });
  },
);

onMounted(() => {
  // expande automaticamente o grupo que contém a rota atual (sem colapsar os
  // já abertos, já que o estado é compartilhado entre as telas)
  sidebarMenu.value.forEach((entry) => {
    if (entry.children?.some((child) => isMenuActive(child.to))) {
      expandedMenus.value = new Set(expandedMenus.value).add(entry.label);
    }
  });

  // restaura a posição de scroll da sidebar após o Shell remontar
  nextTick(() => {
    if (asideRef.value) {
      asideRef.value.scrollTop = scrollTop.value;
    }
  });
});
</script>

<template>
  <!-- Overlay fixo por cima do site (o "Perfil" do Figma é um painel/modal,
       não uma página dentro do layout). O Teleport pro body + Transition de
       abrir/fechar ficam no Modal.vue (que monta este Shell) — não podem
       ficar aqui: Transition não anima um componente cuja raiz é um
       Teleport quando os dois estão em componentes diferentes
       (vuejs/core#5836). -->
  <div
    ref="overlayRef"
    class="user-v2-bg fixed inset-0 z-[90] overflow-y-auto overflow-x-hidden overscroll-contain font-sans text-white"
    @click.self="onBackdropClick"
    @dragstart.prevent
  >
    <div
      class="flex min-h-full w-full flex-col md:mx-0 md:ml-auto md:mr-0 md:max-w-[948px] md:justify-start"
      @click.self="onBackdropClick"
    >
      <div
        class="border-solid user-v2-panel relative flex flex-col md:h-screen md:overflow-hidden md:rounded-tl-[20px] md:border md:border-b-0 md:border-v2-primary"
      >
        <!-- fechar no mobile: agora é o X da própria barra do topo do
             UserV2Header (Figma "nav"), não mais um botão flutuante aqui —
             evita o painel cobrir o header real do site (ver histórico) -->
        <UserV2Header />

        <!-- Desktop: card preto (fundo/borda do Figma) com respiro de 16px
               nas laterais, igual ao botão X — no mobile fica sem margem,
               usando o fundo único do painel. -->
        <div
          class="border-solid flex flex-1 flex-col md:min-h-0 md:mx-[16px] md:rounded-t-[20px] md:border md:border-b-0 md:border-v2-primary md:bg-v2-default-200"
        >
          <!-- Desktop: barra de saldos sempre visível, full-width acima de
                 sidebar+conteúdo (no mobile os saldos ficam só no Perfil). -->
          <div class="hidden px-[16px] pt-[16px] md:block">
            <UserV2BalancesBar />
          </div>

          <div
            class="flex flex-col gap-[16px] px-[16px] pb-[16px] pt-[16px] md:min-h-0 md:flex-1 md:flex-row md:overflow-hidden md:pt-[12px]"
          >
            <aside
              class="hidden w-[280px] shrink-0 flex-col md:flex md:min-h-0"
            >
              <!-- lista rolável: o scroll acontece SÓ aqui, acima do rodapé -->
              <div
                ref="asideRef"
                class="no-scrollbar flex flex-1 flex-col gap-[6px] md:min-h-0 md:overflow-x-hidden md:overflow-y-auto"
                @scroll="onSidebarScroll"
              >
                <p
                  class="shrink-0 px-[4px] pb-[8px] text-[12px] font-medium leading-[16px] text-white"
                >
                  Edite seus dados
                </p>
                <template
                  v-for="entry in sidebarMenu"
                  :key="entry.label"
                >
                  <UserV2MenuItem
                    :active="!entry.children && isMenuActive(entry.to)"
                    :coming-soon="entry.comingSoon"
                    :expandable="!!entry.children"
                    :expanded="expandedMenus.has(entry.label)"
                    :icon="entry.icon"
                    :label="entry.label"
                    :to="entry.to"
                    @click="
                      entry.children && !entry.to && toggleMenu(entry.label)
                    "
                    @toggle="toggleMenu(entry.label)"
                  />
                  <template
                    v-if="entry.children && expandedMenus.has(entry.label)"
                  >
                    <UserV2MenuItem
                      v-for="child in entry.children"
                      :key="child.label"
                      :active="isMenuActive(child.to)"
                      :icon="child.icon"
                      :label="child.label"
                      nested
                      :to="child.to"
                    />
                  </template>
                </template>
              </div>

              <!-- Sair da conta: fixo no rodapé da sidebar -->
              <button
                class="mt-[8px] flex h-[40px] w-full shrink-0 items-center justify-center gap-[8px] rounded-[12px] bg-v2-danger/20 text-[14px] text-v2-danger"
                @click="isLogoutConfirmOpen = true"
              >
                <UserV2Icon name="logout" />
                Sair da conta
              </button>
            </aside>

            <main class="flex min-w-0 flex-1 flex-col gap-[12px] md:min-h-0">
              <!-- tabs só no mobile; no desktop a navegação é a sidebar -->
              <UserV2Tabs
                :active="activeTab"
                class="md:hidden"
                :notifications-visible="isNotificationsVisible"
              />

              <div
                v-if="props.screenTitle"
                class="flex items-center gap-[8px] text-[14px] leading-[20px] text-white"
              >
                <NuxtLink
                  class="flex items-center md:hidden"
                  :to="localeLink(props.backTo)"
                >
                  <UserV2Icon name="chevron-left" />
                </NuxtLink>
                {{ props.screenTitle }}
              </div>

              <!-- no desktop, a rolagem fica só aqui dentro (painel tem altura máxima) -->
              <div
                ref="contentRef"
                class="no-scrollbar flex flex-col gap-[12px] overscroll-contain md:min-h-0 md:flex-1 md:overflow-y-auto"
              >
                <slot />
              </div>

              <button
                class="mt-[8px] flex h-[40px] w-full items-center justify-center gap-[8px] rounded-[12px] bg-v2-danger/20 text-[14px] text-v2-danger md:hidden"
                @click="isLogoutConfirmOpen = true"
              >
                <UserV2Icon name="logout" />
                Sair da conta
              </button>
            </main>
          </div>
        </div>

        <!-- Modal de resgate de conquista (overlay compartilhado; aberto
               pelo dashboard ou pela tela Conquistas via composable) -->
        <UserV2AchievementModal />
      </div>
    </div>

    <UserV2LogoutConfirmModal
      v-model="isLogoutConfirmOpen"
      @confirm="handleLogout"
    />
  </div>
</template>

<style scoped lang="scss">
// Mobile: overlay ocupa a tela inteira com o fundo do design.
// Desktop: overlay vira backdrop escurecido e o gradiente vai pro painel.
.user-v2-bg {
  background-color: rgb(var(--v2-background));
  background-image: radial-gradient(
    circle at 100% 0%,
    rgb(var(--v2-default-300)) 0%,
    rgb(var(--v2-default-200)) 35%,
    rgb(var(--v2-default-300)) 70%,
    rgb(var(--v2-default-200)) 100%
  );

  @media (min-width: 768px) {
    background-color: rgb(0 7 23 / 0.75);
    background-image: none;
    backdrop-filter: blur(6px);
  }
}

// (estilos da .user-v2-scroll são globais — assets/css/user-v2-tokens.scss)

.user-v2-panel {
  @media (min-width: 768px) {
    // mesmo azul do header (colors/base/primary-200) — aparece nos 16px de
    // respiro ao redor do card preto (ver components/user-v2/Shell.vue)
    background-color: rgb(var(--v2-primary-200));
  }
}
</style>

<!-- Transição de abrir/fechar do painel (nome "user-v2-slide" aplicado pelo
     <Transition> em components/user-v2/Modal.vue, que é quem monta/desmonta
     a tela ativa) — NÃO pode ser scoped: as classes de transição são
     atribuídas pelo Modal.vue, um componente diferente deste.

     Usa @keyframes/animation (e não transition) de propósito: o reset global
     em assets/css/main.scss mata `transition` no app inteiro com !important
     (`*:not(.swiper-wrapper):not(circle):not(.transition)`), então qualquer
     transition aqui precisaria de hack de especificidade e ainda assim o Vue
     media duração zero às vezes. `animation` passa ilesa pelo reset.

     Mesma curva/duração do slide-up do MobilePreview e da roletinha, só na
     horizontal: o painel entra da direita e sai pra direita. -->
<style lang="scss">
// o Vue decide quando a transição "terminou" pela duração do elemento RAIZ —
// por isso o fundo usa a mesma duração do painel
.user-v2-slide-enter-active {
  animation: user-v2-overlay-fade 0.32s cubic-bezier(0.22, 0.61, 0.36, 1)
    forwards;
}
.user-v2-slide-leave-active {
  animation: user-v2-overlay-fade 0.22s cubic-bezier(0.4, 0, 1, 1) reverse
    forwards;
}

.user-v2-slide-enter-active .user-v2-panel {
  animation: user-v2-panel-slide-in 0.32s cubic-bezier(0.22, 0.61, 0.36, 1)
    forwards;
  will-change: transform;
}
.user-v2-slide-leave-active .user-v2-panel {
  animation: user-v2-panel-slide-in 0.22s cubic-bezier(0.4, 0, 1, 1) reverse
    forwards;
  will-change: transform;
}

@keyframes user-v2-overlay-fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

// 100% = a própria largura do painel (100vw no mobile, 948px no desktop),
// sem overshoot — o painel só encosta na borda direita da tela
@keyframes user-v2-panel-slide-in {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
